import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SeverityBadge, StatusBadge } from "@/components/common/Badges";
import { ImpactEvidencePanel } from "@/components/workspaces/ImpactEvidencePanel";
import { TID } from "@/lib/testIds";

function Section({ label, children }) {
    return (
        <div>
            <div className="label-eyebrow mb-2">{label}</div>
            {children}
        </div>
    );
}

export function RecommendationDrawer({ openRec, setOpenRec, review, explain }) {
    return (
        <Sheet open={!!openRec} onOpenChange={(o) => !o && setOpenRec(null)}>
            <SheetContent className="bg-[var(--bg-elevated)] border-l border-cyan-500/20 text-slate-200 w-full sm:max-w-2xl overflow-y-auto">
                {openRec && (
                    <div>
                        <SheetHeader>
                            <div className="flex items-center gap-2 mb-2">
                                <SeverityBadge severity={openRec.risk_rating} />
                                <StatusBadge status={openRec.status} />
                            </div>
                            <SheetTitle className="font-display text-white text-xl">{openRec.title}</SheetTitle>
                            <SheetDescription className="text-slate-400">
                                Explainable engineering recommendation produced by ASSEP.
                            </SheetDescription>
                        </SheetHeader>

                        <div className="mt-6 space-y-5">
                            <Section label="Root Cause"><p className="text-sm text-slate-300 leading-relaxed">{openRec.root_cause}</p></Section>
                            <Section label="Engineering Explanation"><p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{openRec.explanation}</p></Section>
                            <Section label="Rationale"><p className="text-sm text-slate-300 leading-relaxed">{openRec.rationale}</p></Section>
                            <Section label="Remediation Steps">
                                <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside">
                                    {openRec.remediation_steps?.map((s, i) => <li key={i}>{s}</li>)}
                                </ol>
                            </Section>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="panel px-3 py-2">
                                    <div className="label-eyebrow">Risk</div>
                                    <div className="font-display text-lg text-rose-400 capitalize mt-1">{openRec.risk_rating}</div>
                                </div>
                                <div className="panel px-3 py-2">
                                    <div className="label-eyebrow">Effort</div>
                                    <div className="font-display text-lg text-cyan-300 capitalize mt-1">{openRec.estimated_effort}</div>
                                </div>
                                <div className="panel px-3 py-2">
                                    <div className="label-eyebrow">Confidence</div>
                                    <div className="font-display text-lg text-emerald-400 mt-1">{Math.round((openRec.confidence_score || 0) * 100)}%</div>
                                </div>
                            </div>
                            <Section label="Affected SAP Objects">
                                <div className="flex flex-wrap gap-2">
                                    {openRec.affected_objects?.length ? openRec.affected_objects.map((o) => (
                                        <span key={o} className="font-mono text-[11px] px-2 py-1 border border-cyan-500/20 text-cyan-300 rounded-sm">{o}</span>
                                    )) : <span className="text-xs text-slate-500">—</span>}
                                </div>
                            </Section>
                            <Section label="Business Impact"><p className="text-sm text-slate-300">{openRec.business_impact || "—"}</p></Section>
                            <Section label="References">
                                <ul className="text-xs text-slate-400 space-y-1">
                                    {openRec.references?.length ? openRec.references.map((r, i) => <li key={`${openRec.id}-ref-${i}`}>• {r}</li>) : <li>—</li>}
                                </ul>
                            </Section>
                            {openRec.impact_evidence && (
                                <Section label="Change Impact Evidence">
                                    <ImpactEvidencePanel rec={openRec} onUpdated={(fresh) => setOpenRec(fresh)} />
                                </Section>
                            )}

                            <div className="flex flex-wrap gap-2 pt-4 border-t border-white/5">
                                <button
                                    data-testid={TID.recommendationApprove(openRec.id)}
                                    onClick={() => review(openRec.id, "approve")}
                                    className="btn-primary flex items-center gap-1.5"
                                >
                                    Approve
                                </button>
                                <button
                                    data-testid={TID.recommendationReject(openRec.id)}
                                    onClick={() => review(openRec.id, "reject")}
                                    className="btn-ghost text-rose-300 border-rose-500/30 hover:border-rose-500/60"
                                >
                                    Reject
                                </button>
                                <button
                                    data-testid={TID.recommendationExplain(openRec.id)}
                                    onClick={() => explain(openRec.id)}
                                    className="btn-ghost ml-auto"
                                >
                                    Refresh AI Explanation
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
